import { useCallback, useMemo, useState } from 'react';
import { Button, DatePicker, Select, Space, Table, Tag, Typography } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table/interface';
import { CheckOutlined, ApartmentOutlined } from '@ant-design/icons';
import { Link, useNavigate, type NavigateFunction } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';

import {
  useTrainingEmployeesInfinite,
  useEmployeeAssignments,
  TRAINING_EMPLOYEES_PAGE_SIZE,
  type TrainingEmployeesListFilters,
} from '@/entities/training/api/libraryApi';
import {
  programKindLabels,
  assignmentStatusLabels,
  assignmentStatusValues,
  type TrainingEmployeeRow,
  type AssignmentStatus,
  type ProgramKind,
  type EmployeeAssignment,
} from '@/entities/training/model/librarySchema';
import { trainingAssignmentStatusTagColor } from '@/entities/training/model/trainingTableTagColors';
import { antTableScroll } from '@/shared/constants/antTable';
import { SearchInput } from '@/shared/ui/SearchInput';
import { OrgTreeDrawer } from '@/pages/Employees/OrgTreeDrawer';
import { OrgStructureSelectDrawer } from '@/shared/ui/OrgStructureSelectDrawer';
import sharedExpanded from '@/shared/ui/ExpandedRows/ExpandedRows.module.scss';

import s from './RecordsTab.module.scss';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_MULTI_OPTIONS = assignmentStatusValues.map((v) => ({
  label: assignmentStatusLabels[v],
  value: v,
}));

const QUICK_FILTERS = [
  { label: 'Просроченные', value: 'overdue' },
  { label: 'Истекает в 30 дней', value: 'expires_in_30_days' },
  { label: 'Не допущенные', value: 'not_admitted' },
] as const;

type QuickKey = (typeof QUICK_FILTERS)[number]['value'] | '';

export type RecordsTabProps = {
  focusEmployeeId?: number;
  /** Вкладка «Проблемные сотрудники»: только быстрый фильтр not_admitted. */
  problemEmployeesOnly?: boolean;
};

function ExpandedAssignments({ employeeId }: { employeeId: string }) {
  const { data, isLoading } = useEmployeeAssignments(employeeId);

  if (isLoading) return <Text type="secondary">Загрузка...</Text>;
  if (!data?.length) return <Text type="secondary">Нет назначений</Text>;

  return (
    <div className={sharedExpanded.expanded}>
      <Text className={s['root__expanded-hint']}>Обучения и инструктажи сотрудника:</Text>
      {data.map((a: EmployeeAssignment) => (
        <Link
          key={a.id}
          to={`/training/records/${a.id}`}
          className={`${sharedExpanded.row} ${s['root__expanded-link']}`}
        >
          <span className={s['root__expanded-type']}>
            {a.program.name}
            {a.level ? ` — ${a.level.level_name}` : ''}
          </span>
          <span className={sharedExpanded.meta}>
            {a.planned_at ? `Дата: ${a.planned_at}` : ''}
            {a.valid_until ? ` · До: ${a.valid_until}` : ''}
            {a.completed_at ? ` · Пройдено: ${a.completed_at}` : ''}
          </span>
          <Tag color={trainingAssignmentStatusTagColor[a.status] ?? 'default'}>
            {assignmentStatusLabels[a.status as AssignmentStatus] ?? a.status}
          </Tag>
        </Link>
      ))}
    </div>
  );
}

type RecordsEmployeesTableProps = {
  filters: TrainingEmployeesListFilters;
  navigate: NavigateFunction;
};

/** Смонтирован заново при смене `filters` (см. `key` у родителя) — индекс страницы сбрасывается без эффектов. */
function RecordsEmployeesTable({ filters, navigate }: RecordsEmployeesTableProps) {
  const { data, isLoading, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useTrainingEmployeesInfinite(filters);

  const [pageIndex, setPageIndex] = useState(0);
  const loadedPageCount = data?.pages.length ?? 0;
  const maxLoadedIndex = Math.max(0, loadedPageCount - 1);
  const viewIndex = Math.min(pageIndex, maxLoadedIndex);

  const employees = useMemo(() => data?.pages[viewIndex]?.items ?? [], [data?.pages, viewIndex]);
  const itemsOnPage = employees.length;
  const canGoForward = viewIndex < loadedPageCount - 1 || Boolean(hasNextPage);
  const paginationTotal = canGoForward
    ? (viewIndex + 1) * TRAINING_EMPLOYEES_PAGE_SIZE + 1
    : viewIndex * TRAINING_EMPLOYEES_PAGE_SIZE + itemsOnPage;

  const showPagination = !(isLoading && loadedPageCount === 0) && (itemsOnPage > 0 || canGoForward);

  const handleTableChange = useCallback(
    async (pag: TablePaginationConfig) => {
      const idx = (pag.current ?? 1) - 1;
      if (idx < 0 || idx === viewIndex) return;
      if (idx < viewIndex) {
        setPageIndex(idx);
        return;
      }
      if (idx < loadedPageCount) {
        setPageIndex(idx);
        return;
      }
      if (idx === loadedPageCount && hasNextPage && !isFetchingNextPage) {
        await fetchNextPage();
        setPageIndex(idx);
      }
    },
    [viewIndex, loadedPageCount, hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const columns: ColumnsType<TrainingEmployeeRow> = [
    {
      title: 'ФИО',
      dataIndex: 'full_name',
      key: 'name',
      render: (name: string) => <span className={s['root__link-cell']}>{name}</span>,
    },
    {
      title: 'Табельный №',
      dataIndex: 'personnel_number',
      key: 'tab',
      width: 120,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Должность',
      key: 'pos',
      render: (_: unknown, row: TrainingEmployeeRow) => row.position?.name ?? '—',
    },
    {
      title: 'Подразделение',
      key: 'dept',
      render: (_: unknown, row: TrainingEmployeeRow) => row.org_unit.name,
    },
    {
      title: 'Допуск',
      dataIndex: 'work_admission',
      key: 'admission',
      width: 130,
      render: (val: string) => {
        const isAdmitted = val === 'admitted';
        return <Tag color={isAdmitted ? 'success' : 'error'}>{isAdmitted ? 'Допущен' : 'Не допущен'}</Tag>;
      },
    },
    {
      title: 'Критичный статус',
      key: 'status',
      width: 160,
      render: (_: unknown, row: TrainingEmployeeRow) => {
        const da = row.displayed_assignment;
        if (!da) return <Tag>—</Tag>;
        return (
          <Tag color={trainingAssignmentStatusTagColor[da.status] ?? 'default'}>
            {assignmentStatusLabels[da.status as AssignmentStatus] ?? da.status}
          </Tag>
        );
      },
    },
    {
      title: 'Программа',
      key: 'program',
      ellipsis: true,
      render: (_: unknown, row: TrainingEmployeeRow) => {
        const da = row.displayed_assignment;
        if (!da) return '—';
        return (
          <>
            {da.program_name}
            {da.program_kind ? (
              <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                ({programKindLabels[da.program_kind as ProgramKind] ?? da.program_kind})
              </Text>
            ) : null}
          </>
        );
      },
    },
    {
      title: 'Действует до',
      key: 'valid',
      width: 130,
      render: (_: unknown, row: TrainingEmployeeRow) => row.displayed_assignment?.valid_until ?? '—',
    },
  ];

  const listLoading = isLoading || (isFetching && !isFetchingNextPage);

  return (
    <Table
      loading={listLoading}
      dataSource={employees}
      columns={columns}
      rowKey="employee_id"
      size="small"
      pagination={
        showPagination
          ? {
              current: viewIndex + 1,
              pageSize: TRAINING_EMPLOYEES_PAGE_SIZE,
              total: paginationTotal,
              showSizeChanger: false,
              showTotal: (_total, range) => `${range[0]}–${range[1]}`,
              disabled: isFetchingNextPage,
            }
          : false
      }
      onChange={handleTableChange}
      scroll={antTableScroll}
      expandable={{
        expandedRowRender: (row: TrainingEmployeeRow) => <ExpandedAssignments employeeId={row.employee_id} />,
      }}
      onRow={(record) => ({
        onClick: (e) => {
          const target = e.target as HTMLElement | null;
          if (!target) return;
          if (target.closest('button, a, .ant-table-row-expand-icon, .ant-table-expand-icon')) return;
          navigate(`/training/employee/${record.employee_id}`);
        },
        className: s['root__clickable-row'],
      })}
    />
  );
}

export function RecordsTab({ focusEmployeeId: _focusEmployeeId, problemEmployeesOnly }: RecordsTabProps) {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [quickFilter, setQuickFilter] = useState<QuickKey>(problemEmployeesOnly ? 'not_admitted' : '');
  const [orgDrawerOpen, setOrgDrawerOpen] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState<{ id: string; name: string } | null>(null);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    const trimmed = val.trim();
    if (trimmed.length >= 2 || trimmed.length === 0) {
      setDebouncedSearch(trimmed.length >= 2 ? trimmed : '');
    }
  }, []);

  const filters: TrainingEmployeesListFilters = useMemo(() => {
    const p: TrainingEmployeesListFilters = {};
    if (debouncedSearch) p.q = debouncedSearch;
    if (departmentFilter) p.orgUnitId = [departmentFilter.id];
    if (statusFilter.length > 0 && !statusFilter.includes('not_admitted')) p.status = statusFilter;
    if (dateRange?.[0]) p.validUntilFrom = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) p.validUntilTo = dateRange[1].format('YYYY-MM-DD');
    const hasNotAdmitted = statusFilter.includes('not_admitted');
    const qf = problemEmployeesOnly ? 'not_admitted' : hasNotAdmitted ? 'not_admitted' : quickFilter;
    if (qf) p.quickFilter = [qf];
    return p;
  }, [debouncedSearch, departmentFilter, statusFilter, dateRange, quickFilter, problemEmployeesOnly]);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const handleOrgSelect = (id: string, name: string, opts?: { keepDrawerOpen?: boolean }) => {
    setDepartmentFilter({ id, name });
    if (!opts?.keepDrawerOpen) {
      setOrgDrawerOpen(false);
    }
  };

  const handleQuickClick = (key: QuickKey) => {
    if (problemEmployeesOnly) return;
    setQuickFilter((prev) => (prev === key ? '' : key));
    setStatusFilter([]);
  };

  const selectIcon = useMemo(() => <CheckOutlined style={{ color: 'var(--o500)' }} />, []);

  return (
    <>
      {problemEmployeesOnly ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Сотрудники с отказом в допуске к работе по данным учёта обучения.
        </Text>
      ) : null}

      <Space wrap className={s.root__toolbar}>
        <SearchInput
          placeholder="Поиск по ФИО, табельному №..."
          className={s['root__filter-input']}
          value={search}
          onChange={handleSearchChange}
          allowClear
        />
        <Select
          mode="multiple"
          className={s['root__filter-select-status']}
          placeholder="Статус"
          value={statusFilter}
          disabled={problemEmployeesOnly}
          onChange={(v) => {
            setStatusFilter(v);
            setQuickFilter('');
          }}
          options={STATUS_MULTI_OPTIONS}
          allowClear
          maxTagCount={0}
          maxTagPlaceholder={(omitted) =>
            omitted.length === 1 ? (omitted[0].label as string) : `${omitted.length} статуса`
          }
          menuItemSelectedIcon={selectIcon}
        />
        <RangePicker
          placeholder={['Действует с', 'Действует до']}
          format="DD.MM.YYYY"
          value={dateRange}
          onChange={(v) => setDateRange(v)}
          allowClear
          style={{ width: 260 }}
        />
        <Button icon={<ApartmentOutlined size={16} />} onClick={() => setOrgDrawerOpen(true)}>
          Подразделение
        </Button>
      </Space>

      {departmentFilter ? (
        <div className={s['root__active-tag']}>
          <Text type="secondary" className={s['root__tag-label']}>
            Подразделение:
          </Text>
          <Tag closable onClose={() => setDepartmentFilter(null)}>
            {departmentFilter.name}
          </Tag>
        </div>
      ) : null}

      {!problemEmployeesOnly ? (
        <Space wrap className={s.root__presets}>
          {QUICK_FILTERS.map((p) => (
            <Tag
              key={p.value}
              className={`${s.root__preset} ${quickFilter === p.value ? s.root__preset_active : ''}`}
              onClick={() => handleQuickClick(p.value)}
            >
              {p.label}
            </Tag>
          ))}
        </Space>
      ) : null}

      <RecordsEmployeesTable key={filtersKey} filters={filters} navigate={navigate} />

      <OrgStructureSelectDrawer open={orgDrawerOpen} onClose={() => setOrgDrawerOpen(false)} width={420}>
        <OrgTreeDrawer onSelect={handleOrgSelect} />
      </OrgStructureSelectDrawer>
    </>
  );
}
